import React, { PropsWithChildren, useEffect } from "react";
import { Outlet, useNavigate, useParams } from "react-router-dom";

import { BasicDataWorkspaceFragment } from "@/hooks/queries.graphql";
import useBasicData from "@/hooks/useBasicData";
import { getConnectionNodesTyped } from "@/utils/relay";
import {
  hasUsablePermissions,
  isHiddenHierarchyParent,
} from "@/utils/workspacePermissions";

import { clearCache } from "../graphql/cache";
import { useNavigationStore } from "../nav/state";

const OrderByTier: Record<string, number> = {
  FREE: 0,
  PRO: 1,
  STUDIO: 2,
  ENTERPRISE: 3,
};

interface WorkspaceMembership {
  __typename: "WorkspaceMembership";
  id: string;
  workspace: BasicDataWorkspaceFragment;
}

const orderMembershipsByTier = (
  memberships: WorkspaceMembership[],
): WorkspaceMembership[] => {
  const sortedMemberShips = [...memberships];
  sortedMemberShips.sort((a, b) => {
    const aVal = OrderByTier[a.workspace.planTier ?? "FREE"] ?? 0;
    const bVal = OrderByTier[b.workspace.planTier ?? "FREE"] ?? 0;

    if (aVal === bVal) {
      return 0;
    }

    return aVal > bVal ? -1 : 1;
  });
  return sortedMemberShips;
};

export const WorkspaceChecker: React.FC<PropsWithChildren> = ({
  children = <Outlet />,
}) => {
  const params = useParams<{ workspace?: string }>();
  const { user } = useBasicData();
  const urlWorkspaceName = params.workspace;
  const { activeWorkspaceName, setActiveWorkspaceName } = useNavigationStore();
  const navigate = useNavigate();

  useEffect(() => {
    // Assigns active workspace in to local storage only if user is switching to a context of a workspace that they are part of
    // If there is no active workspace assigned, we use the first workspace in user's memberships

    if (user) {
      const allMemberships = getConnectionNodesTyped(user.memberships);
      const memberships = allMemberships.filter(
        (m) => !isHiddenHierarchyParent(m.workspace),
      );

      const isPartOfUrlWorkspace = memberships.some(
        ({ workspace }) => workspace.name === urlWorkspaceName,
      );

      // Handles not set yet case, logged out case, and when user gets removed from workspace
      const isPartOfActiveWorkspace = memberships.some(
        ({ workspace }) => workspace.name === activeWorkspaceName,
      );

      // URL points to a hidden hierarchy parent — redirect away
      const urlIsHiddenParent =
        !isPartOfUrlWorkspace &&
        urlWorkspaceName &&
        allMemberships.some(
          ({ workspace }) => workspace.name === urlWorkspaceName,
        );
      if (urlIsHiddenParent) {
        let target: string | undefined;
        if (isPartOfActiveWorkspace) {
          target = activeWorkspaceName!;
        } else {
          const usableMemberships = memberships.filter((m) =>
            hasUsablePermissions(m.workspace),
          );
          const candidates =
            usableMemberships.length > 0 ? usableMemberships : memberships;
          const ordered = orderMembershipsByTier(candidates);
          target = ordered[0]?.workspace?.name;
        }
        if (target) {
          setActiveWorkspaceName(target);
          navigate(`/${target}`, { replace: true });
          clearCache();
        } else {
          navigate("/new", { replace: true });
        }
        return;
      }

      if (!isPartOfActiveWorkspace) {
        const usableMemberships = memberships.filter((m) =>
          hasUsablePermissions(m.workspace),
        );
        const candidates =
          usableMemberships.length > 0 ? usableMemberships : memberships;
        const membershipsOrderedByTier = orderMembershipsByTier(candidates);
        const firstWorkspaceName = membershipsOrderedByTier[0]?.workspace?.name;

        if (!firstWorkspaceName) {
          // If no workspace is found, redirect to the new workspace page
          navigate("/new");
          return;
        }

        setActiveWorkspaceName(firstWorkspaceName);
        clearCache();
      } else if (
        isPartOfUrlWorkspace &&
        urlWorkspaceName !== activeWorkspaceName
      ) {
        const urlWorkspace = memberships.find(
          ({ workspace }) => workspace.name === urlWorkspaceName,
        )?.workspace;

        if (
          urlWorkspaceName &&
          urlWorkspace &&
          hasUsablePermissions(urlWorkspace)
        ) {
          setActiveWorkspaceName(urlWorkspaceName);
          clearCache();
        } else if (
          urlWorkspaceName &&
          urlWorkspace &&
          !hasUsablePermissions(urlWorkspace)
        ) {
          // User navigated to a workspace they have no real permissions for — redirect to a usable one
          const usableMemberships = memberships.filter((m) =>
            hasUsablePermissions(m.workspace),
          );
          const candidates =
            usableMemberships.length > 0 ? usableMemberships : memberships;
          const membershipsOrderedByTier = orderMembershipsByTier(candidates);
          const fallbackName = membershipsOrderedByTier[0]?.workspace?.name;
          if (fallbackName) {
            navigate(`/${fallbackName}`, { replace: true });
            setActiveWorkspaceName(fallbackName);
            clearCache();
          }
        }
      }
    }
  }, [user, urlWorkspaceName]);

  return children;
};
